import React, {
  ComponentType,
  Context,
  ForwardRefExoticComponent,
  PropsWithoutRef,
  Provider,
  RefAttributes,
  forwardRef,
  useContext,
} from 'react';

type InjectedContextProp<Name extends string, Value> = Record<Name, Value>;

type WithContext<Value, Name extends string> = <ComponentProps extends InjectedContextProp<Name, Value>>(
  component: ComponentType<ComponentProps>
) => ForwardRefExoticComponent<PropsWithoutRef<Omit<ComponentProps, Name>> & RefAttributes<unknown>>;

type ContextHelpers<Value, Name extends string> = Record<`${Capitalize<Name>}Provider`, Provider<Value>> &
  Record<`${Name}Context`, Context<Value>> &
  Record<`use${Capitalize<Name>}`, () => Value> &
  Record<`with${Capitalize<Name>}`, WithContext<Value, Name>>;

export default function createContextHelpers<Value, Name extends string>(
  contextValue: Context<Value>,
  name: Name
): ContextHelpers<Value, Name> {
  const capitalizedName = `${name.charAt(0).toUpperCase()}${name.slice(1)}`;
  const { Provider: contextProvider } = contextValue;

  function useMethod(): Value {
    return useContext(contextValue);
  }

  const withMethod: WithContext<Value, Name> = (component) =>
    forwardRef<unknown, Omit<React.ComponentProps<typeof component>, Name>>((props, ref) => {
      const value = useMethod();
      // TypeScript cannot prove that a computed generic key plus `Omit<ComponentProps,
      // Name>` reconstructs `ComponentProps`. This is the one dynamic boundary this helper
      // exists to provide; every caller still gets an exact injected-prop contract.
      const componentProps = ({ ...props, [name]: value } as unknown) as React.ComponentProps<typeof component>;
      return React.createElement(component, { ...componentProps, ref });
    });

  // Computed template-literal keys are widened to `string` in object-literal inference.
  // The mapped return type describes those exact runtime keys and is verified by the tests.
  return {
    [`${name}Context`]: contextValue,
    [`${capitalizedName}Provider`]: contextProvider,
    [`use${capitalizedName}`]: useMethod,
    [`with${capitalizedName}`]: withMethod,
  } as ContextHelpers<Value, Name>;
}
